import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Divider,
  Input,
  Select,
  SelectItem,
  Textarea,
  Spinner,
} from "@nextui-org/react";
import {
  Briefcase,
  Calendar,
  Clock,
  Code,
  DollarSign,
  FileText,
  MapPin,
  Tag,
  Users,
  Wand2,
} from "lucide-react";
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { createJobPost, getJobPostsCount, getCompanyById } from "../../services/companyService";
import PremiumPlanPopup from "./PremiumPlanPopup";

const jobTypes = [
  { value: "Full-time", label: "Full-time" },
  { value: "Part-time", label: "Part-time" },
  { value: "Contract", label: "Contract" },
  { value: "Internship", label: "Internship" },
  { value: "Remote", label: "Remote" },
];

export default function PostJob() {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    jobTitle: "",
    location: "",
    jobType: "",
    salary: "",
    jobDescription: "",
    skillsRequired: "",
    duration: "",
    department: "",
    openings: "",
    applicationDeadline: "",
    expiryDate: "",
  });

  const [skills, setSkills] = useState([]);
  const [jobPostsThisMonth, setJobPostsThisMonth] = useState(0);
  const [error, setError] = useState("");
  const [showPremiumPopup, setShowPremiumPopup] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [minExpiryDate, setMinExpiryDate] = useState("");

  // Fetch job posts count and subscription status
  const fetchJobPostsCountAndSubscription = useCallback(async () => {
    try {
      const count = await getJobPostsCount(user.uid);
      setJobPostsThisMonth(count);

      // Fetch company data to check subscription status
      const companyData = await getCompanyById(user.uid);
      if (companyData?.subscription) {
        const subscription = companyData.subscription;
        const expiresAt = subscription.expiresAt.toDate();
        const now = new Date();

        if (expiresAt > now) {
          setHasActiveSubscription(true);
        } else {
          setHasActiveSubscription(false);
        }
      } else {
        setHasActiveSubscription(false);
      }
    } catch (error) {
      console.error("Error fetching job posts count or subscription:", error);
      setError("Failed to fetch job posts count or subscription status. Please try again later.");
    }
  }, [user.uid]);

  useEffect(() => {
    if (user) {
      fetchJobPostsCountAndSubscription();
    }
  }, [user, fetchJobPostsCountAndSubscription]);

  // Set minimum expiry date based on application deadline
  useEffect(() => {
    if (formData.applicationDeadline) {
      const deadlineDate = new Date(formData.applicationDeadline);
      deadlineDate.setDate(deadlineDate.getDate() + 1); // Expiry date must be at least 1 day after deadline
      setMinExpiryDate(deadlineDate.toISOString().split('T')[0]);
    }
  }, [formData.applicationDeadline]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSkillsChange = (value) => {
    if (value.includes(",")) {
      const newSkills = value.split(",").map((skill) => skill.trim());
      setSkills((prev) => [
        ...new Set([...prev, ...newSkills.filter((s) => s)]),
      ]);
      handleChange("skillsRequired", "");
    } else {
      handleChange("skillsRequired", value);
    }
  };

  const removeSkill = (skillToRemove) => {
    setSkills(skills.filter((skill) => skill !== skillToRemove));
  };

  // Generate job description using Gemini API
  const generateJobDescription = async () => {
    if (!formData.jobTitle || skills.length === 0) {
      setError("Please provide a job title and at least one skill to generate a description.");
      return;
    }

  // Add this check
  // if (!process.env.NEXT_PUBLIC_GEMINI_API_KEY) {
  //   setError("API key is not configured. Please contact support.");
  //   return;
  // }
  
    setIsGeneratingDescription(true);
    setError("");
  
    try {
      const GEMINI_API_KEY = "AIzaSyDCWFyZc5OR9qmbX-NQYVsQtvy7d5qX6Zo"; 
      // const GEMINI_API_KEY = process.env.NEXT_PUBLIC_GEMINI_API_KEY;
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${GEMINI_API_KEY}`;
  
      const prompt = `Generate a professional job description for a ${formData.jobTitle} position. 
      The required skills are: ${skills.join(", ")}. 
      Include sections for:
      - Job Summary
      - Key Responsibilities
      - Required Qualifications
      - Preferred Skills
      - Benefits (if applicable)
      Make it engaging and professional, about 200-300 words.`;
  
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: prompt
            }]
          }]
        }),
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Gemini API Error:", errorData);
        throw new Error(errorData.error?.message || "Failed to generate description");
      }
  
      const data = await response.json();
      const generatedText = data.candidates?.[0]?.content?.parts?.[0]?.text || 
                         "Could not generate description. Please try again.";
  
      setFormData(prev => ({
        ...prev,
        jobDescription: generatedText
      }));
    } catch (error) {
      console.error("Error generating description:", error);
      setError(`Failed to generate description: ${error.message}. Please try again later.`);
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Check if the user has reached the limit of 2 posts per month and has no active subscription
      if (jobPostsThisMonth >= 2 && !hasActiveSubscription) {
        setShowPremiumPopup(true);
        setError("You have reached the limit of 2 job posts per month. Please upgrade to a premium plan to post more jobs.");
        return;
      }

      // Set current date as post date and status as "active"
      const currentDate = new Date().toISOString();
      
      // If expiry date is not set, default to 30 days from now
      let expiryDate = formData.expiryDate;
      if (!expiryDate) {
        const defaultExpiry = new Date();
        defaultExpiry.setDate(defaultExpiry.getDate() + 30);
        expiryDate = defaultExpiry.toISOString();
      }

      const submissionData = {
        ...formData,
        jobType: Array.isArray(formData.jobType)
          ? formData.jobType[0]
          : formData.jobType,
        skillsRequired: skills,
        postDate: currentDate,
        expiryDate: expiryDate,
        status: "active" // initial status
      };

      await createJobPost(submissionData, user.uid);
      setFormData({
        jobTitle: "",
        location: "",
        jobType: "",
        salary: "",
        jobDescription: "",
        skillsRequired: "",
        applicationDeadline: "",
        department: "",
        openings: "",
        duration: "",
        expiryDate: ""
      });
      setSkills([]);
      setJobPostsThisMonth((prev) => prev + 1);
      setError("");
    } catch (error) {
      console.error("Error posting job:", error);
      setError("Failed to post job. Please try again.");
    }
  };

  // Handle plan selection (redirect to payment page)
  const handlePlanSelect = (planId) => {
    setShowPremiumPopup(false);
    window.location.href = `/payment?planId=${planId}&companyId=${user.uid}`;
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen p-3 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-primary-500 to-primary-800 bg-clip-text text-transparent">
              Post a New Job
            </h1>
            <p className="text-default-500 mt-1">
              Fill in the details to create an attractive job listing
            </p>
          </div>
        </div>

        <Card className="shadow-md hover:shadow-lg transition-shadow duration-300 bg-gradient-to-r from-white to-gray-50">
          <CardHeader className="flex flex-col gap-2 items-center px-6 pt-6 pb-2">
            <div className="bg-primary-100 p-3 rounded-full">
              <Briefcase className="w-6 h-6 text-primary-500" />
            </div>
            <h2 className="text-xl font-bold">Job Details</h2>
            <p className="text-default-500 text-sm text-center">
              Create a compelling job listing to attract the best candidates
            </p>
          </CardHeader>
          <Divider className="my-2" />
          <CardBody className="p-6">
            {error && (
              <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Job Title"
                  placeholder="e.g., Senior Software Engineer"
                  value={formData.jobTitle}
                  onChange={(e) => handleChange("jobTitle", e.target.value)}
                  startContent={
                    <Briefcase className="w-4 h-4 text-default-400" />
                  }
                  variant="bordered"
                  classNames={{
                    label: "text-default-600 font-medium",
                  }}
                  isRequired
                />
                <Input
                  label="Openings"
                  type="number"
                  placeholder="e.g., 5"
                  value={formData.openings}
                  onChange={(e) => handleChange("openings", e.target.value)}
                  startContent={<Users className="w-4 h-4 text-default-400" />}
                  variant="bordered"
                  classNames={{
                    label: "text-default-600 font-medium",
                  }}
                  isRequired
                />

                <Input
                  label="Duration"
                  placeholder="e.g., 6 months"
                  value={formData.duration}
                  onChange={(e) => handleChange("duration", e.target.value)}
                  startContent={<Clock className="w-4 h-4 text-default-400" />}
                  variant="bordered"
                  classNames={{
                    label: "text-default-600 font-medium",
                  }}
                  isRequired
                />

                <Input
                  type="date"
                  label="Application Deadline"
                  placeholder="Select a date"
                  value={
                    formData.applicationDeadline
                      ? new Date(formData.applicationDeadline)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  onChange={(e) =>
                    handleChange("applicationDeadline", e.target.value)
                  }
                  startContent={
                    <Calendar className="w-4 h-4 text-default-400" />
                  }
                  variant="bordered"
                  classNames={{
                    label: "text-default-600 font-medium",
                  }}
                  isRequired
                />

                <Input
                  type="date"
                  label="Job Expiry Date"
                  placeholder="Select expiry date"
                  value={
                    formData.expiryDate
                      ? new Date(formData.expiryDate)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  onChange={(e) =>
                    handleChange("expiryDate", e.target.value)
                  }
                  min={minExpiryDate}
                  startContent={
                    <Calendar className="w-4 h-4 text-default-400" />
                  }
                  variant="bordered"
                  classNames={{
                    label: "text-default-600 font-medium",
                  }}
                  description="Date when this job post will automatically close"
                />

                <Input
                  label="Department"
                  placeholder="e.g., Engineering"
                  value={formData.department}
                  onChange={(e) => handleChange("department", e.target.value)}
                  startContent={<Tag className="w-4 h-4 text-default-400" />}
                  variant="bordered"
                  classNames={{
                    label: "text-default-600 font-medium",
                  }}
                />

                <Input
                  label="Location"
                  placeholder="e.g., New York, NY"
                  value={formData.location}
                  onChange={(e) => handleChange("location", e.target.value)}
                  startContent={<MapPin className="w-4 h-4 text-default-400" />}
                  variant="bordered"
                  classNames={{
                    label: "text-default-600 font-medium",
                  }}
                  isRequired
                />

                <Select
                  label="Job Type"
                  placeholder="Select job type"
                  selectedKeys={[formData.jobType]}
                  onChange={(e) => handleChange("jobType", e.target.value)}
                  startContent={<Clock className="w-4 h-4 text-default-400" />}
                  variant="bordered"
                  classNames={{
                    label: "text-default-600 font-medium",
                  }}
                  isRequired
                >
                  {jobTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </Select>

                <Input
                  label="Salary Range"
                  placeholder="e.g., $80,000 - $100,000"
                  value={formData.salary}
                  onChange={(e) => handleChange("salary", e.target.value)}
                  startContent={
                    <DollarSign className="w-4 h-4 text-default-400" />
                  }
                  variant="bordered"
                  classNames={{
                    label: "text-default-600 font-medium",
                  }}
                />

                <Input
                  label="Skills Required"
                  placeholder="Add skills (separate with comma)"
                  value={formData.skillsRequired}
                  onChange={(e) => handleSkillsChange(e.target.value)}
                  startContent={<Code className="w-4 h-4 text-default-400" />}
                  variant="bordered"
                  classNames={{
                    label: "text-default-600 font-medium",
                  }}
                />
              </div>

              {skills.length > 0 && (
                <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="w-full mb-1 text-sm text-default-600 font-medium">
                    Required Skills:
                  </div>
                  {skills.map((skill, index) => (
                    <Chip
                      key={index}
                      onClose={() => removeSkill(skill)}
                      variant="flat"
                      color="primary"
                      className="shadow-sm"
                    >
                      {skill}
                    </Chip>
                  ))}
                </div>
              )}

              <div className="relative">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-default-600 font-medium">Job Description</label>
                  <Button
                    size="sm"
                    variant="flat"
                    color="secondary"
                    startContent={isGeneratingDescription ? null : <Wand2 className="w-4 h-4" />}
                    onPress={generateJobDescription}
                    isDisabled={isGeneratingDescription || !formData.jobTitle || skills.length === 0}
                  >
                    {isGeneratingDescription ? (
                      <>
                        <Spinner size="sm" color="current" className="mr-2" />
                        Generating...
                      </>
                    ) : (
                      "Generate Description"
                    )}
                  </Button>
                </div>
                <Textarea
                  placeholder="Enter detailed job description or generate one automatically"
                  value={formData.jobDescription}
                  onChange={(e) => handleChange("jobDescription", e.target.value)}
                  variant="bordered"
                  minRows={4}
                  startContent={
                    <FileText className="w-4 h-4 text-default-400 absolute top-3" />
                  }
                  classNames={{
                    input: "pl-8",
                  }}
                  isRequired
                />
              </div>

              <div className="flex justify-center md:justify-end gap-3 pb-6 md:pb-0">
                <Button
                  type="submit"
                  color="primary"
                  size="lg"
                  className="font-semibold bg-gradient-to-r from-primary-600 to-primary-700 shadow-md"
                >
                  Post Job
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      </div>

      {/* Premium Plan Popup */}
      {showPremiumPopup && (
        <PremiumPlanPopup
          isOpen={showPremiumPopup}
          onClose={() => setShowPremiumPopup(false)}
          onPlanSelect={handlePlanSelect}
        />
      )}
    </div>
  );
}